import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { 
  Smartphone, 
  Wrench, 
  Home, 
  List, 
  Plus, 
  Users, 
  LogOut, 
  Settings,
  BarChart3,
  Calendar,
  Package,
  MessageSquare,
  UserCircle,
  CreditCard
} from 'lucide-react';

const Navigation = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const navItems = [
    { path: '/admin', label: 'Tableau de bord', icon: Home },
    { path: '/repairs', label: 'Réparations', icon: List },
    { path: '/repairs/new', label: 'Nouvelle réparation', icon: Plus },
    { path: '/appointments', label: 'Rendez-vous', icon: Calendar },
    { path: '/contacts', label: 'Demandes', icon: MessageSquare },
    { path: '/statistics', label: 'Statistiques', icon: BarChart3 },
  ];

  if (user?.role === 'admin') {
    navItems.push(
      { path: '/products', label: 'Produits', icon: Package },
      { path: '/payments', label: 'Paiements', icon: CreditCard },
      { path: '/users', label: 'Utilisateurs', icon: Users },
      { path: '/settings', label: 'Paramètres', icon: Settings }
    );
  }

  return (
    <nav className="bg-white shadow-sm border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-white" />
                <Wrench className="w-4 h-4 text-white absolute -bottom-0.5 -right-0.5 bg-orange-500 rounded-full p-0.5" />
              </div>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-800">Smart Service Mobile</h1>
              <p className="text-xs text-slate-500">Gestion des Réparations</p>
            </div>
          </Link>

          {/* Navigation Items */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                  data-testid={`nav-${item.path.replace(/[^a-zA-Z0-9]/g, '-')}`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            <div className="hidden sm:block">
              <Link to="/profile" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-900">{user?.username}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                </div>
                <div className="w-8 h-8 bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </Link>
            </div>
            
            <Link to="/profile" className="sm:hidden">
              <Button
                variant="outline"
                size="sm"
                className="text-slate-600 hover:text-slate-900"
              >
                <UserCircle className="w-4 h-4" />
              </Button>
            </Link>
            
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="text-slate-600 hover:text-slate-900"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline ml-2">Déconnexion</span>
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden py-2 border-t border-slate-200">
          <div className="flex flex-wrap gap-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;